import {
  PartnerProductCreateDTO,
  PartnerProductDTO,
  PartnerProductUpdateDTO,
} from 'services/repositories/masterdata/masterdata';
import { ColumnTypesInternal } from 'support/types/webediTable';

/**
 * The array path both `DataTableProvider`'s `name` and `DataTable`'s `meta.dataPrefix` must share —
 * field ids are `${PRODUCTS_DATA_PREFIX}.${rowIndex}.${path}`. Deliberately avoids `line_items`,
 * `transport_units` and `modification_groups`, which shared WebEDI cell code string-matches for
 * packing-list-only behavior.
 */
export const PRODUCTS_DATA_PREFIX = 'products';

/**
 * `name`, `gtin` and `articleNumber` are not flat fields on `PartnerProductDTO` — they live inside
 * `attributes`/`identifiers`, keyed by these domain/name strings. There is no enum for them in the
 * generated schema, so this is their single source of truth in the portal.
 */
export const PRODUCT_IDENTIFIER_DOMAINS = {
  gtin: 'GTIN',
  articleNumber: 'SENDER_INTERNAL',
} as const;

/**
 * How "mark for deletion" is stored on a row. `DeleteCell` — the table's remove button — has one
 * soft-delete mechanism, built for a document's line items: it sets a numeric "quantity" field of
 * the row to 0, offers Restore while that field differs from the row's `ordered_quantity`, and puts
 * `ordered_quantity` back on restore. A product has no quantity, so it borrows the mechanism with a
 * pair of stand-in fields: `kept` is 1 while the product stays and 0 once it is marked, and
 * `ordered_quantity` is always 1 (the name is `DeleteCell`'s, not ours).
 */
export const PRODUCT_KEPT_FIELD = 'kept';

export const PRODUCT_ATTRIBUTE_NAMES = {
  name: 'name',
} as const;

/** The flat shape `DataTable` registers fields against: `products.<rowIndex>.<path>`. */
export type ProductRow = {
  id: string;
  gtin: string;
  name: string;
  articleNumber: string;
  unit_of_measure: string;
  is_orderable: boolean;
  unit_price: number | null;
  tax_rate: number | null;
  currency: string;
  latest_log: PartnerProductDTO['latest_log'];
  /**
   * The full, unedited identifier/attribute collections, carried through so a save can rebuild
   * them whole. The PATCH endpoint replaces a product's *entire* set of identifiers (or
   * attributes) when either is included in the payload — a domain or attribute name this table
   * doesn't expose (e.g. `RECEIVER_INTERNAL`, or an attribute other than `name`) would otherwise
   * be silently deleted the moment any field on the row is edited. None of these register with
   * the form (no cell reads or writes them), so they never affect edit detection — only
   * `buildProductMasterdataSavePlan` reads them.
   */
  identifiers: PartnerProductDTO['identifiers'];
  attributes: PartnerProductDTO['attributes'];
  /**
   * The un-defaulted orderable flag, kept alongside the always-boolean `is_orderable` the UI binds
   * to. Only read by `buildProductMasterdataSavePlan` (on the baseline row), to tell "the server had
   * no opinion" apart from "the user touched this cell" — the UI-facing `is_orderable` can't tell
   * those apart once it's been defaulted to `false`.
   */
  originalIsOrderable: boolean | null;
  /**
   * The ids of every bundle part nested under this product, at any depth. Another read-only
   * passenger: deleting a bundle deletes its parts server-side, so a save must not PATCH a part
   * whose bundle is going away (the part is already gone by then, and the whole PATCH would 404).
   * Empty for a product that isn't a bundle.
   */
  partIds: Array<string>;
  /** 1 while the product stays, 0 once it is marked for deletion — see `PRODUCT_KEPT_FIELD`. */
  [PRODUCT_KEPT_FIELD]: number;
  /** Always 1: what `DeleteCell` puts `kept` back to on Restore. */
  ordered_quantity: number;
  /**
   * WebEDI's marker for a row the user added in this session (`isProcurosAddedValue`). It is what
   * tells a save to POST the row instead of PATCHing it — a new row has no `id` yet.
   */
  _internal?: { [ColumnTypesInternal.procuros_added]?: boolean };
};

/**
 * `id` rides along as a row field, not a `getRowId` — the WebEDI table contract keeps rows keyed
 * by array index (see `docs/decisions/webedi.md`), so a non-integer row id would break every
 * register path. It exists so a patch payload can identify which product an edited row belongs to.
 */
export const toProductRow = (product: PartnerProductDTO): ProductRow => ({
  id: product.id,
  gtin: findIdentifierByDomain(product.identifiers, PRODUCT_IDENTIFIER_DOMAINS.gtin)?.value ?? '',
  name: product.attributes.find((attribute) => attribute.name === PRODUCT_ATTRIBUTE_NAMES.name)?.value ?? '',
  articleNumber: findIdentifierByDomain(product.identifiers, PRODUCT_IDENTIFIER_DOMAINS.articleNumber)?.value ?? '',
  unit_of_measure: product.unit_of_measure,
  // The UI only ever presents Yes/No — there is no way to display or re-save an "unset" state —
  // so a null orderable flag reads as false for display. `originalIsOrderable` keeps the real
  // value around so a save doesn't write this default back for a row nothing touched.
  is_orderable: product.is_orderable ?? false,
  originalIsOrderable: product.is_orderable ?? null,
  unit_price: product.unit_price ?? null,
  tax_rate: product.tax_rate ?? null,
  currency: product.currency,
  latest_log: product.latest_log,
  identifiers: product.identifiers,
  attributes: product.attributes,
  partIds: collectPartIds(product.parts ?? []),
  [PRODUCT_KEPT_FIELD]: 1,
  ordered_quantity: 1,
});

export const toProductRows = (products: Array<PartnerProductDTO>): Array<ProductRow> => products.map(toProductRow);

export type ProductMasterdataFormValues = {
  [PRODUCTS_DATA_PREFIX]: Array<ProductRow>;
};

/**
 * The API returns only top-level products with nested `parts` (bundle components), up to 3 levels
 * deep. Recursively flattens them into sibling rows so every part gets its own row in the table.
 */
export const flattenProducts = (products: Array<PartnerProductDTO>): Array<PartnerProductDTO> =>
  products.flatMap((product) => [product, ...flattenProducts(product.parts ?? [])]);

export const DEFAULT_PRODUCT_CURRENCY = 'EUR';
const DEFAULT_PRODUCT_UNIT_OF_MEASURE = 'EA';

/**
 * A blank row for "Add product". Currency follows the catalogue's existing products (a partner
 * almost always prices everything in one currency), falling back to EUR for an empty catalogue;
 * price stays empty on purpose — it is required, and a made-up default would be saved silently.
 */
export const buildNewProductRow = (currency: string = DEFAULT_PRODUCT_CURRENCY): ProductRow => ({
  id: '',
  gtin: '',
  name: '',
  articleNumber: '',
  unit_of_measure: DEFAULT_PRODUCT_UNIT_OF_MEASURE,
  is_orderable: true,
  originalIsOrderable: null,
  unit_price: null,
  tax_rate: null,
  currency,
  latest_log: null,
  identifiers: [],
  attributes: [],
  partIds: [],
  [PRODUCT_KEPT_FIELD]: 1,
  ordered_quantity: 1,
  _internal: { [ColumnTypesInternal.procuros_added]: true },
});

export type InvalidNewRowField = 'unit_price' | 'unit_of_measure' | 'currency';

export type SavePlan = {
  /** Ids of saved products the user marked for deletion, or removed from the table. */
  deleteIds: Array<string>;
  updates: Array<PartnerProductUpdateDTO>;
  creates: Array<PartnerProductCreateDTO>;
  /**
   * For each entry of `updates` / `creates`, the index of the table row it was built from. A 422
   * from the batch endpoints keys its errors by position *in the payload* (`products.1.unit_price`),
   * and the payload skips untouched rows, so this is what maps an error back to the row to show it on.
   */
  updateRowIndexes: Array<number>;
  createRowIndexes: Array<number>;
  /**
   * New rows missing a field the create endpoint requires. `rowNumber` counts the new rows only
   * (1-based), not the table: added rows sit at the bottom of a long, virtualized table with no
   * index column, so "row 347" would mean scrolling and counting, while "new product 2" is the
   * second one the user added.
   */
  invalidNewRows: Array<{ rowNumber: number; fields: Array<InvalidNewRowField> }>;
};

/**
 * Works out what a save has to send by comparing the form's rows with `baselineRows` — the rows the
 * form was last reset to — matched by product `id`, never by array position.
 *
 * Not `formState.dirtyFields`: RHF derives array dirtiness index-by-index against the defaults, so
 * once "remove row" exists, every row below the removed one is compared with its predecessor's
 * defaults and reports as edited. A save would then PATCH rows nobody touched (and log an edit
 * for each). Matching by id makes a removal affect only the row that was removed.
 *
 * Only rows that differ from their baseline are updated, and each with every field — matching the
 * bulk-edit table's contract: edit a handful of rows, save exactly those.
 */
export const buildProductMasterdataSavePlan = (rows: Array<ProductRow>, baselineRows: Array<ProductRow>): SavePlan => {
  const baselineById = new Map(baselineRows.map((row) => [row.id, row]));
  const remainingIds = new Set(rows.filter((row) => !isNewRow(row) && !isMarkedForDeletion(row)).map((row) => row.id));

  const deletedRows = baselineRows.filter((row) => !remainingIds.has(row.id));
  const deletedBundlePartIds = new Set(deletedRows.flatMap((row) => row.partIds));

  const updates: Array<PartnerProductUpdateDTO> = [];
  const updateRowIndexes: Array<number> = [];
  const creates: Array<PartnerProductCreateDTO> = [];
  const createRowIndexes: Array<number> = [];
  const invalidNewRows: SavePlan['invalidNewRows'] = [];
  let newRowCount = 0;

  rows.forEach((row, rowIndex) => {
    if (isNewRow(row)) {
      newRowCount += 1;
      const fields = findMissingRequiredFields(row);
      if (fields.length > 0) {
        invalidNewRows.push({ rowNumber: newRowCount, fields });
      } else {
        creates.push(toProductCreatePayload(row));
        createRowIndexes.push(rowIndex);
      }
      return;
    }

    const baseline = baselineById.get(row.id);
    if (baseline && !isMarkedForDeletion(row) && !deletedBundlePartIds.has(row.id) && isRowEdited(row, baseline)) {
      updates.push(toProductUpdatePayload(row, baseline));
      updateRowIndexes.push(rowIndex);
    }
  });

  // A product created by an earlier attempt of this same save is a saved row the baseline has never
  // heard of; marking it must still delete it.
  const markedCreatedRows = rows.filter(
    (row) => !isNewRow(row) && isMarkedForDeletion(row) && row.id && !baselineById.has(row.id),
  );

  return {
    deleteIds: [...deletedRows, ...markedCreatedRows].map((row) => row.id),
    updates,
    creates,
    updateRowIndexes,
    createRowIndexes,
    invalidNewRows,
  };
};

/** A cleared number input is `NaN`; the baseline holds `null` for "no value". Both mean the same thing. */
const normalizeNumber = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const normalizeText = (value: string | null | undefined): string => value ?? '';

const isRowEdited = (row: ProductRow, baseline: ProductRow): boolean =>
  normalizeText(row.gtin) !== normalizeText(baseline.gtin) ||
  normalizeText(row.name) !== normalizeText(baseline.name) ||
  normalizeText(row.articleNumber) !== normalizeText(baseline.articleNumber) ||
  normalizeText(row.unit_of_measure) !== normalizeText(baseline.unit_of_measure) ||
  normalizeText(row.currency) !== normalizeText(baseline.currency) ||
  row.is_orderable !== baseline.is_orderable ||
  normalizeNumber(row.unit_price) !== normalizeNumber(baseline.unit_price) ||
  normalizeNumber(row.tax_rate) !== normalizeNumber(baseline.tax_rate);

const findMissingRequiredFields = (row: ProductRow): Array<InvalidNewRowField> => {
  const missing: Array<InvalidNewRowField> = [];
  if (normalizeNumber(row.unit_price) === null) {
    missing.push('unit_price');
  }
  if (!normalizeText(row.unit_of_measure)) {
    missing.push('unit_of_measure');
  }
  if (!normalizeText(row.currency)) {
    missing.push('currency');
  }
  return missing;
};

const toProductCreatePayload = (row: ProductRow): PartnerProductCreateDTO => ({
  unit_price: row.unit_price as number,
  tax_rate: normalizeNumber(row.tax_rate),
  unit_of_measure: row.unit_of_measure as PartnerProductCreateDTO['unit_of_measure'],
  // Always sent, unlike on update: a new product has no prior value to protect, and the UI's
  // Yes/No is exactly what the user chose.
  is_orderable: row.is_orderable,
  currency: row.currency,
  identifiers: buildIdentifiersPayload(row),
  attributes: buildAttributesPayload(row),
});

const toProductUpdatePayload = (row: ProductRow, baseline: ProductRow): PartnerProductUpdateDTO => {
  const { unit_price: unitPrice, tax_rate: taxRate } = row;

  return {
    id: row.id,
    // A cleared price becomes `NaN` (see `valueAsNumber` on the number cell), and the schema has
    // no `null` for `unit_price` (unlike `tax_rate`) — omitting it leaves the server's value
    // unchanged. There is currently no way through this endpoint to explicitly clear a set price.
    ...(Number.isFinite(unitPrice) ? { unit_price: unitPrice as number } : {}),
    tax_rate: Number.isFinite(taxRate) ? (taxRate as number) : null,
    unit_of_measure: row.unit_of_measure as PartnerProductUpdateDTO['unit_of_measure'],
    // Sent only if the cell itself was changed, or the product already had a real (non-null)
    // value — the server treats a changed scalar as a genuine edit (and logs it as one), so a
    // `null → false` default the user never interacted with must not be sent as if it were.
    ...(row.is_orderable !== baseline.is_orderable || baseline.originalIsOrderable !== null
      ? { is_orderable: row.is_orderable }
      : {}),
    currency: row.currency,
    identifiers: buildIdentifiersPayload(row),
    attributes: buildAttributesPayload(row),
  };
};

type IdentifierUpdate = NonNullable<PartnerProductUpdateDTO['identifiers']>[number];
type AttributeUpdate = NonNullable<PartnerProductUpdateDTO['attributes']>[number];

/**
 * Rebuilds a row's full identifier list for the payload: every identifier other than the ones
 * `toProductRow` actually displayed (GTIN, article number) is carried through unchanged — a
 * product can legitimately hold more than one identifier per domain (the backend diffs by the
 * full `(domain, value, domain_partner_id)` tuple, not "one per domain"), so filtering by *domain*
 * instead of by the specific displayed identifier would silently delete every sibling the table
 * never showed. GTIN/article number are replaced with the row's current values, or dropped
 * entirely if the field was cleared, which is how this endpoint removes an identifier ("entries
 * not repeated are removed").
 *
 * A `domain` outside the endpoint's five known values would only reach here from data the backend
 * itself returned, which the backend's own validation is the right place to catch — not
 * client-side guessing.
 */
const buildIdentifiersPayload = (row: ProductRow): Array<IdentifierUpdate> => {
  const displayedGtin = findIdentifierByDomain(row.identifiers, PRODUCT_IDENTIFIER_DOMAINS.gtin);
  const displayedArticleNumber = findIdentifierByDomain(row.identifiers, PRODUCT_IDENTIFIER_DOMAINS.articleNumber);

  const preserved: Array<IdentifierUpdate> = row.identifiers
    .filter((identifier) => identifier.id !== displayedGtin?.id && identifier.id !== displayedArticleNumber?.id)
    .map((identifier) => ({
      domain: identifier.domain as IdentifierUpdate['domain'],
      value: identifier.value,
      domain_partner_id: identifier.domain_partner_id,
    }));

  const edited: Array<IdentifierUpdate> = [];
  if (row.gtin) {
    edited.push({
      domain: PRODUCT_IDENTIFIER_DOMAINS.gtin,
      value: row.gtin,
      domain_partner_id: displayedGtin?.domain_partner_id ?? null,
    });
  }
  if (row.articleNumber) {
    edited.push({
      domain: PRODUCT_IDENTIFIER_DOMAINS.articleNumber,
      value: row.articleNumber,
      domain_partner_id: displayedArticleNumber?.domain_partner_id ?? null,
    });
  }

  return [...preserved, ...edited];
};

/**
 * Same reconstruction as identifiers, for the single attribute this table exposes: every other
 * attribute name is preserved as-is, and `name` is replaced or dropped based on the row's current
 * value.
 */
const buildAttributesPayload = (row: ProductRow): Array<AttributeUpdate> => {
  const preserved: Array<AttributeUpdate> = row.attributes
    .filter((attribute) => attribute.name !== PRODUCT_ATTRIBUTE_NAMES.name)
    .map((attribute) => ({ name: attribute.name, value: attribute.value }));

  return row.name ? [...preserved, { name: PRODUCT_ATTRIBUTE_NAMES.name, value: row.name }] : preserved;
};

/**
 * The single source of truth for "which identifier does this domain's column display" — shared by
 * `toProductRow` (reading it) and `buildIdentifiersPayload` (knowing which one to replace), so the
 * two can never disagree about which identifier is "the displayed one" when a product holds more
 * than one per domain.
 */
const findIdentifierByDomain = (identifiers: PartnerProductDTO['identifiers'], domain: string) =>
  identifiers.find((identifier) => identifier.domain === domain);

const collectPartIds = (parts: Array<PartnerProductDTO>): Array<string> =>
  parts.flatMap((part) => [part.id, ...collectPartIds(part.parts ?? [])]);

const isNewRow = (row: ProductRow): boolean => Boolean(row._internal?.[ColumnTypesInternal.procuros_added]);

const isMarkedForDeletion = (row: ProductRow): boolean => row[PRODUCT_KEPT_FIELD] === 0;
